import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { BatchStatusBadge, PackageStatusBadge } from "@/components/StatusBadge";
import { Truck, CheckCircle2, Send, PackageCheck, Trash2, Ban, Printer, Loader2 } from "lucide-react";
import { useWarehousePermissions } from "@/hooks/useWarehousePermissions";
import { openPrintWindow } from "@/lib/printHelper";

export default function BatchDetail() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { can } = useWarehousePermissions();
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/warehouse/batches/${batchId}`);
      setBatch(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [batchId]);

  const run = async (msg, fn) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      setConfirm(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const removePkg = async (pid) => {
    try {
      await api.post(`/warehouse/batches/${batchId}/remove-package/${pid}`);
      toast.success("Removed from batch.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="py-14 text-center text-slate-500">Loading…</div>;
  if (!batch) return <div className="py-14 text-center text-slate-500">Not found</div>;

  const canRemove = !["SHIPPED_OUT_FROM_WAREHOUSE", "READY_FOR_LAST_MILE_PICKUP", "COMPLETED"].includes(batch.status) && can("remove_from_batch");
  const canMarkReady = ["DRAFT", "OPEN_FOR_ADDING_PACKAGES"].includes(batch.status) && can("mark_batch_ready");
  const canShipOut = ["READY_FOR_DISPATCH", "OPEN_FOR_ADDING_PACKAGES", "DRAFT"].includes(batch.status) && can("mark_batch_shipped");
  const canMarkLastMile = ["SHIPPED_OUT_FROM_WAREHOUSE", "READY_FOR_DISPATCH"].includes(batch.status) && can("mark_last_mile");
  const canCancel = !["SHIPPED_OUT_FROM_WAREHOUSE", "READY_FOR_LAST_MILE_PICKUP", "COMPLETED"].includes(batch.status) && can("edit_batch");
  const canPrint = can("print_manifest");

  const printManifest = () => {
    const html = manifestHtml(batch);
    const w = openPrintWindow(html, "width=900,height=700");
    if (!w) { toast.error("Pop-up blocked"); return; }
    setTimeout(() => { try { w.focus(); w.print(); } catch (_) { /* user closed window */ } }, 400);
  };

  return (
    <div data-testid="wh-batch-detail" className="space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-1">/ Batch</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{batch.batch_name}</h1>
          <div className="font-mono-tech text-xs text-cyan-300 mt-1">{batch.batch_code}</div>
        </div>
        <BatchStatusBadge status={batch.status} />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Info label="Warehouse" value={batch.warehouse_name} />
        <Info label="Destination" value={batch.destination_area || "—"} />
        <Info label="Route" value={batch.route || "—"} />
        <Info label="Dispatch Date" value={batch.dispatch_date || "—"} />
        <Info label="Packages" value={batch.total_packages || 0} />
        <Info label="Created By" value={batch.created_by_name} />
        <Info label="Shipped At" value={batch.shipped_out_at ? new Date(batch.shipped_out_at).toLocaleString() : "—"} />
        <Info label="Last-Mile Ready At" value={batch.ready_for_last_mile_at ? new Date(batch.ready_for_last_mile_at).toLocaleString() : "—"} />
      </div>

      {batch.notes && (
        <div className="border border-slate-200 dark:border-slate-800 rounded-lg p-3 bg-slate-100/40 dark:bg-slate-900/40 text-sm text-slate-700 dark:text-slate-300">
          <div className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500 mb-1">Notes</div>
          {batch.notes}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {canPrint && (
          <Button data-testid="wh-batch-print" onClick={printManifest} variant="outline" className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white">
            <Printer className="w-4 h-4 mr-2" /> Print Manifest
          </Button>
        )}
        {canMarkReady && (
          <Button
            data-testid="wh-batch-mark-ready"
            onClick={() => setConfirm({
              title: "Mark Ready for Dispatch",
              msg: "All packages in this batch will be flagged as outbound-batch-ready.",
              label: "Mark Ready",
              fn: () => run("Batch ready for dispatch.", () => api.post(`/warehouse/batches/${batchId}/mark-ready`)),
            })}
            className="bg-teal-400 text-slate-950 hover:bg-teal-300"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" /> Mark Ready for Dispatch
          </Button>
        )}
        {canShipOut && (
          <Button
            data-testid="wh-batch-ship-out"
            onClick={() => setConfirm({
              title: "Ship Out Batch",
              msg: "Are you sure you want to mark this outbound batch as shipped out from the warehouse?",
              label: "Confirm Shipped Out",
              fn: () => run("Batch shipped out.", () => api.post(`/warehouse/batches/${batchId}/ship-out`)),
            })}
            className="bg-orange-400 text-slate-950 hover:bg-orange-300"
          >
            <Send className="w-4 h-4 mr-2" /> Mark Shipped Out
          </Button>
        )}
        {canMarkLastMile && (
          <Button
            data-testid="wh-batch-last-mile"
            onClick={() => setConfirm({
              title: "Ready for Last-Mile",
              msg: "Packages will be released to riders for last-mile pickup.",
              label: "Confirm Ready",
              fn: () => run("Ready for last-mile pickup.", () => api.post(`/warehouse/batches/${batchId}/mark-ready-last-mile`)),
            })}
            className="bg-emerald-400 text-slate-950 hover:bg-emerald-300"
          >
            <PackageCheck className="w-4 h-4 mr-2" /> Ready for Last-Mile
          </Button>
        )}
        {canCancel && (
          <Button
            data-testid="wh-batch-cancel"
            onClick={() => setConfirm({
              title: "Cancel Batch",
              msg: "This will release all packages back to sorting.",
              label: "Cancel Batch",
              variant: "danger",
              fn: () => run("Batch cancelled.", () => api.post(`/warehouse/batches/${batchId}/cancel`)),
            })}
            variant="outline"
            className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-red-400 hover:bg-red-500/10 hover:text-red-300"
          >
            <Ban className="w-4 h-4 mr-2" /> Cancel Batch
          </Button>
        )}
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <Truck className="w-4 h-4 text-cyan-400" />
          <h2 className="font-display font-semibold">Packages in this Batch</h2>
        </div>
        {(batch.packages || []).length === 0 ? (
          <div className="border border-dashed border-slate-200 dark:border-slate-800 rounded-lg py-10 text-center text-slate-500">
            No packages yet.
          </div>
        ) : (
          <div className="space-y-2">
            {batch.packages.map(pkg => (
              <div
                key={pkg.id}
                data-testid={`wh-batch-pkg-${pkg.package_id}`}
                className="border border-slate-200 dark:border-slate-800 rounded-lg p-3 bg-slate-100/40 dark:bg-slate-900/40 flex items-start gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-mono-tech text-xs text-cyan-300 truncate">{pkg.qr_code_id}</div>
                    <PackageStatusBadge status={pkg.status} />
                  </div>
                  <div className="font-display text-sm font-semibold truncate">{pkg.package_name}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 truncate">
                    {pkg.store_name} → {pkg.receiver_name} · {pkg.receiver_address}
                  </div>
                </div>
                {canRemove && (
                  <Button
                    data-testid={`wh-batch-remove-${pkg.package_id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => removePkg(pkg.package_id)}
                    className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white">
          <DialogHeader>
            <DialogTitle>{confirm?.title}</DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400">{confirm?.msg}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white">Cancel</Button>
            <Button
              data-testid="wh-batch-confirm-action"
              onClick={() => confirm?.fn?.()}
              disabled={busy}
              className={confirm?.variant === "danger" ? "bg-red-500 text-slate-900 dark:text-white hover:bg-red-400" : "bg-cyan-400 text-slate-950 hover:bg-cyan-300"}
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {confirm?.label}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="border border-slate-200 dark:border-slate-800 rounded-lg p-3 bg-slate-100/40 dark:bg-slate-900/40">
      <div className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-sm text-slate-900 dark:text-white">{value || "—"}</div>
    </div>
  );
}

function manifestHtml(batch) {
  const rows = (batch.packages || []).map((p, i) => `
    <tr>
      <td>${i + 1}</td>
      <td>${escapeHtml(p.qr_code_id || "")}</td>
      <td>${escapeHtml(p.package_id || "")}</td>
      <td>${escapeHtml(p.store_name || "")}</td>
      <td>${escapeHtml(p.receiver_name || "")}</td>
      <td>${escapeHtml(p.receiver_phone || "")}</td>
      <td>${escapeHtml(p.receiver_address || "")}</td>
      <td>${escapeHtml(p.package_name || "")}</td>
    </tr>
  `).join("");
  return `<!doctype html><html><head><meta charset="utf-8"><title>${batch.batch_code} · Manifest</title>
  <style>
    body{font:13px -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#0f172a;padding:24px;}
    h1{font-size:18px;margin:0 0 4px;} .meta{color:#475569;margin-bottom:18px;font-size:12px;}
    table{width:100%;border-collapse:collapse;font-size:11px;} th,td{border:1px solid #cbd5e1;padding:6px 8px;text-align:left;vertical-align:top;}
    th{background:#f1f5f9;font-size:10px;text-transform:uppercase;letter-spacing:.06em;}
    .footer{margin-top:24px;display:flex;justify-content:space-between;font-size:11px;color:#475569;}
    @media print{ .noprint{display:none;} }
  </style></head><body>
  <div class="noprint" style="text-align:right;margin-bottom:8px;">
    <button onclick="window.print()" style="padding:6px 12px;">Print</button>
  </div>
  <h1>${escapeHtml(batch.batch_name)}</h1>
  <div class="meta">
    <b>${escapeHtml(batch.batch_code)}</b> · ${escapeHtml(batch.warehouse_name || "")}
    · Destination: ${escapeHtml(batch.destination_area || "—")}
    · Dispatch: ${escapeHtml(batch.dispatch_date || "—")}
    · Total: ${batch.total_packages || 0}
  </div>
  <table><thead><tr>
    <th>#</th><th>QR</th><th>Package</th><th>Store</th>
    <th>Receiver</th><th>Phone</th><th>Address</th><th>Description</th>
  </tr></thead><tbody>${rows || "<tr><td colspan='8' style='text-align:center;padding:20px;color:#94a3b8;'>No packages</td></tr>"}</tbody></table>
  <div class="footer">
    <div>Prepared by: ${escapeHtml(batch.created_by_name || "")}</div>
    <div>Printed: ${new Date().toLocaleString()}</div>
  </div></body></html>`;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
}
